/**
 * Servicio de Gamificación
 * Maneja niveles, recompensas, racha, estrellas y progreso del usuario
 */

import supabaseService from './supabaseService';
import interactionTimeService from './interactionTimeService';
// import configService from './configService';
import {
  LevelDefinition,
  StreakAchievement,
  LevelProgress,
  AccentType,
  PersonalityType,
} from '../types';

// Definición de niveles y recompensas
export const LEVELS: LevelDefinition[] = [
  { level: 1, timeMinutes: 1, interactions: 1, reward: 'title_pionier' },
  { level: 2, timeMinutes: 10, interactions: 5, reward: 'accessory_glasses' },
  { level: 3, timeMinutes: 30, interactions: 10, reward: 'accent_belgica' },
  { level: 4, timeMinutes: 60, interactions: 20, reward: 'personality_sarcastic' },
  { level: 5, timeMinutes: 90, interactions: 30, reward: 'title_compagnon' },
  { level: 6, timeMinutes: 120, interactions: 40, reward: 'accessory_vest' },
  { level: 7, timeMinutes: 150, interactions: 50, reward: 'pack_draguer' },
  { level: 8, timeMinutes: 180, interactions: 60, reward: 'personality_funny' },
  { level: 9, timeMinutes: 210, interactions: 70, reward: 'pack_insulter' },
  { level: 10, timeMinutes: 240, interactions: 80, reward: 'scholarship' },
];

// Mapeo de acentos y personalidades a niveles requeridos
export const ACCENT_REQUIREMENTS: Record<AccentType, number> = {
  'Francia': 1, // Solo en nivel 1
  'Canadá': 2, // Desbloqueado a partir de nivel 2
  'Australia': 2, // Desbloqueado a partir de nivel 2
  'Bélgica': 3, // Requiere nivel 3
};

export const PERSONALITY_REQUIREMENTS: Record<PersonalityType, number> = {
  'Neutral': 1, // Solo en nivel 1
  'Amigable': 1, // Disponible desde nivel 1
  'Gruñona': 2, // Desbloqueado a partir de nivel 2
  'Sarcástica': 4, // Requiere nivel 4
  'Graciosa': 8, // Requiere nivel 8
};

class GamificationService {
  /**
   * Registrar conexión diaria y actualizar racha
   */
  async registerDailyConnection(email: string): Promise<number> {
    try {
      
      // Registrar conexión del día actual
      const dailyConnection = await supabaseService.registerDailyConnection(email);

      // Obtener conexiones para calcular racha
      const connections = await supabaseService.getDailyConnections(email, 10);
      
      // Calcular racha (días consecutivos)
      const streak = this.calculateStreak(connections);
      
      // Actualizar progreso con nueva racha
      const updated = await supabaseService.updateUserProgress(email, {
        current_streak: streak,
      });

      // Verificar si alcanzó racha de 5 y actualizar contador
      if (streak === 5) {
        await this.checkStreak5Milestone(email, streak);
      }

      return streak;
    } catch (error) {
      console.error('[Gamification] ❌ Error al registrar conexión diaria:', error);
      return 0;
    }
  }

  /**
   * Calcular racha actual basada en conexiones diarias
   */
  calculateStreak(connections: Array<{ connection_date: string }>): number {
    if (connections.length === 0) {
      return 0;
    }

    // Normalizar a YYYY-MM-DD (Supabase puede devolver "2025-02-04" o "2025-02-04T00:00:00.000Z")
    const toDateOnly = (d: string) => (typeof d === 'string' && d.length >= 10 ? d.slice(0, 10) : d);

    // Usar fecha local (no UTC) para coincidir con cómo se guarda
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`; // YYYY-MM-DD


    // Filtrar conexiones futuras (solo considerar hasta hoy)
    const validConnections = connections.filter(c => {
      const connDate = new Date(c.connection_date);
      connDate.setHours(0, 0, 0, 0);
      return connDate <= today;
    });

    if (validConnections.length === 0) {
      return 0;
    }

    // Ordenar por fecha descendente (más reciente primero)
    const sorted = [...validConnections].sort(
      (a, b) => new Date(b.connection_date).getTime() - new Date(a.connection_date).getTime()
    );


    // Verificar si hay conexión de hoy (comparar solo YYYY-MM-DD)
    const hasToday = sorted.some(c => toDateOnly(c.connection_date) === todayStr);
    if (!hasToday) {
      return 0;
    }

    let streak = 0;

    // Calcular días consecutivos desde hoy hacia atrás
    for (let i = 0; i < sorted.length; i++) {
      const connectionDateStr = sorted[i].connection_date;
      const connectionDateOnly = toDateOnly(connectionDateStr);
      // Calcular fecha esperada usando fecha local
      const expectedDate = new Date(today);
      expectedDate.setDate(today.getDate() - i);
      const expectedDateStr = `${expectedDate.getFullYear()}-${String(expectedDate.getMonth() + 1).padStart(2, '0')}-${String(expectedDate.getDate()).padStart(2, '0')}`;


      if (connectionDateOnly === expectedDateStr) {
        streak++;
      } else {
        break;
      }
    }

    // Máximo 5 para visualización
    const finalStreak = Math.min(streak, 5);
    return finalStreak;
  }

  /**
   * Obtener racha actual
   */
  async getCurrentStreak(email: string): Promise<number> {
    try {
      const connections = await supabaseService.getDailyConnections(email, 10);
      return this.calculateStreak(connections);
    } catch (error) {
      console.error('Error al obtener racha:', error);
      return 0;
    }
  }

  /**
   * Obtener número de estrellas (1-5) basado en días de racha
   */
  getStarCount(streak: number): number {
    return Math.min(streak, 5);
  }

  /**
   * Verificar si se alcanzó racha de 5 y actualizar contador
   */
  async checkStreak5Milestone(email: string, currentStreak: number): Promise<boolean> {
    try {
      if (currentStreak !== 5) return false;

      const progress = await supabaseService.getUserProgress(email);
      if (!progress) return false;

      const today = new Date().toISOString().split('T')[0];
      const lastStreak5Date = progress.last_streak_5_date;

      // Solo contar si es la primera vez que alcanza 5 en el día actual
      if (lastStreak5Date !== today) {
        const newCount = (progress.streak_5_count || 0) + 1;
        await supabaseService.updateUserProgress(email, {
          streak_5_count: newCount,
          last_streak_5_date: today,
        });
        return true;
      }

      return false;
    } catch (error) {
      console.error('Error al verificar racha de 5:', error);
      return false;
    }
  }

  /**
   * Verificar si se alcanzó racha de 5 y obtener información del logro
   */
  async checkStreak5Achievement(email: string, currentStreak: number): Promise<StreakAchievement | null> {
    try {
      if (currentStreak !== 5) return null;

      const progress = await supabaseService.getUserProgress(email);
      if (!progress) return null;

      const today = new Date().toISOString().split('T')[0];
      const lastStreak5Date = progress.last_streak_5_date;

      // Solo mostrar si es la primera vez que alcanza 5 en el día actual
      if (lastStreak5Date !== today) {
        return {
          streakDays: 5,
          isStreak5: true,
          streak5Count: (progress.streak_5_count || 0) + 1,
        };
      }

      return null;
    } catch (error) {
      console.error('Error al verificar logro de racha de 5:', error);
      return null;
    }
  }

  /**
   * Obtener información del logro de racha de 5
   */
  async getStreak5AchievementInfo(email: string): Promise<{ streak5Count: number } | null> {
    try {
      const progress = await supabaseService.getUserProgress(email);
      if (!progress) return null;

      return {
        streak5Count: progress.streak_5_count || 0,
      };
    } catch (error) {
      console.error('Error al obtener información de racha de 5:', error);
      return null;
    }
  }

  /**
   * Obtener cuántas veces ha alcanzado racha de 5 días
   */
  async getStreak5Count(email: string): Promise<number> {
    try {
      const progress = await supabaseService.getUserProgress(email);
      return progress?.streak_5_count || 0;
    } catch (error) {
      console.error('Error al obtener contador de racha de 5:', error);
      return 0;
    }
  }

  /**
   * Registrar una interacción
   */
  async registerInteraction(email: string, sessionId?: string): Promise<void> {
    try {
      await supabaseService.registerInteraction(email, 'message', sessionId);

      // Actualizar contador de interacciones en progreso
      const totalInteractions = await supabaseService.getTotalInteractions(email);
      await supabaseService.updateUserProgress(email, {
        total_interactions: totalInteractions,
      });

      // Verificar si subió de nivel
      await this.checkLevelUp(email);
    } catch (error) {
      console.error('Error al registrar interacción:', error);
    }
  }

  /**
   * Obtener cantidad total de interacciones
   */
  async getTotalInteractions(email: string): Promise<number> {
    try {
      return await supabaseService.getTotalInteractions(email);
    } catch (error) {
      console.error('Error al obtener interacciones:', error);
      return 0;
    }
  }

  /**
   * Calcular nivel actual basado en tiempo e interacciones
   */
  calculateCurrentLevel(timeSeconds: number, interactions: number): number {
    const timeMinutes = Math.floor(timeSeconds / 60);

    // Buscar el nivel más alto que cumpla con los requisitos
    for (let i = LEVELS.length - 1; i >= 0; i--) {
      const level = LEVELS[i];
      if (timeMinutes >= level.timeMinutes && interactions >= level.interactions) {
        return level.level;
      }
    }

    return 1; // Nivel mínimo
  }

  /**
   * Obtener nivel actual del usuario
   */
  async getCurrentLevel(email: string): Promise<number> {
    try {
      const progress = await supabaseService.getUserProgress(email);
      if (!progress) return 1;

      // Obtener tiempo total (combinar con interactionTimeService si está disponible)
      // const config = configService.getConfig();
      let totalTimeSeconds = progress.total_time_seconds || 0;

      // Si hay tiempo de la sesión actual, sumarlo
      try {
        const sessionTime = interactionTimeService.getTimeElapsed();
        totalTimeSeconds += sessionTime;
      } catch (error) {
        // Ignorar si interactionTimeService no está disponible
      }

      return this.calculateCurrentLevel(totalTimeSeconds, progress.total_interactions || 0);
    } catch (error) {
      console.error('Error al obtener nivel actual:', error);
      return 1;
    }
  }

  /**
   * Obtener progreso hacia siguiente nivel
   */
  async getLevelProgress(email: string): Promise<LevelProgress | null> {
    try {
      const currentLevel = await this.getCurrentLevel(email);
      const progress = await supabaseService.getUserProgress(email);
      if (!progress) return null;

      const nextLevel = LEVELS.find((l) => l.level === currentLevel + 1);
      if (!nextLevel) {
        // Ya está en el nivel máximo
        return {
          currentLevel,
          nextLevel: currentLevel,
          timeProgress: 0,
          timeRequired: 0,
          interactionsProgress: 0,
          interactionsRequired: 0,
          progressPercentage: 100,
        };
      }

      // Obtener tiempo total
      let totalTimeSeconds = progress.total_time_seconds || 0;
      
      
      try {
        const sessionTime = interactionTimeService.getTimeElapsed();
        totalTimeSeconds += sessionTime;
      } catch (error) {
      }

      const timeMinutes = Math.floor(totalTimeSeconds / 60);
      const totalInteractions = progress.total_interactions || 0;

      const timeProgress = Math.min(timeMinutes, nextLevel.timeMinutes);
      const interactionsProgress = Math.min(totalInteractions, nextLevel.interactions);
      

      // Calcular porcentajes individuales, asegurando que no excedan 100%
      const timeProgressPercent = nextLevel.timeMinutes > 0 
        ? Math.min((timeProgress / nextLevel.timeMinutes) * 100, 100)
        : 100;
      const interactionsProgressPercent = nextLevel.interactions > 0
        ? Math.min((interactionsProgress / nextLevel.interactions) * 100, 100)
        : 100;
      const overallProgress = (timeProgressPercent + interactionsProgressPercent) / 2;
      

      return {
        currentLevel,
        nextLevel: nextLevel.level,
        timeProgress,
        timeRequired: nextLevel.timeMinutes,
        interactionsProgress,
        interactionsRequired: nextLevel.interactions,
        progressPercentage: Math.min(overallProgress, 100),
      };
    } catch (error) {
      console.error('Error al obtener progreso de nivel:', error);
      return null;
    }
  }

  /**
   * Verificar si el usuario subió de nivel y desbloquear recompensas
   */
  async checkLevelUp(email: string): Promise<boolean> {
    try {
      const currentLevel = await this.getCurrentLevel(email);
      const progress = await supabaseService.getUserProgress(email);
      if (!progress) return false;

      if (currentLevel > progress.current_level) {
        // Subió de nivel, desbloquear recompensa
        const newLevel = LEVELS.find((l) => l.level === currentLevel);
        if (newLevel) {
          const unlockedRewards = [...(progress.unlocked_rewards || []), newLevel.reward];
          await supabaseService.updateUserProgress(email, {
            current_level: currentLevel,
            unlocked_rewards: unlockedRewards,
          });
          return true;
        }
      }

      return false;
    } catch (error) {
      console.error('Error al verificar subida de nivel:', error);
      return false;
    }
  }

  /**
   * Obtener recompensas desbloqueadas
   */
  async getUnlockedRewards(email: string): Promise<string[]> {
    try {
      const progress = await supabaseService.getUserProgress(email);
      return progress?.unlocked_rewards || [];
    } catch (error) {
      console.error('Error al obtener recompensas desbloqueadas:', error);
      return [];
    }
  }

  /**
   * Obtener título actual del usuario según nivel
   */
  async getCurrentTitle(email: string): Promise<string | null> {
    try {
      const level = await this.getCurrentLevel(email);
      const rewards = await this.getUnlockedRewards(email);

      if (rewards.includes('title_compagnon') || level >= 5) {
        return 'Compañero / Compagnon';
      } else if (rewards.includes('title_pionier') || level >= 1) {
        return 'Pionero / Pionier';
      }

      return null;
    } catch (error) {
      console.error('Error al obtener título:', error);
      return null;
    }
  }

  /**
   * Verificar si un acento está desbloqueado
   */
  isAccentUnlocked(email: string, accent: AccentType): Promise<boolean> {
    return this.getCurrentLevel(email).then((level) => {
      const requiredLevel = ACCENT_REQUIREMENTS[accent] || 0;
      return level >= requiredLevel;
    });
  }

  /**
   * Verificar si una personalidad está desbloqueada
   */
  isPersonalityUnlocked(email: string, personality: PersonalityType): Promise<boolean> {
    return this.getCurrentLevel(email).then((level) => {
      const requiredLevel = PERSONALITY_REQUIREMENTS[personality] || 0;
      return level >= requiredLevel;
    });
  }

  /**
   * Obtener acentos desbloqueados
   * Francia siempre está desbloqueado
   */
  async getUnlockedAccents(email: string): Promise<AccentType[]> {
    try {
      const level = await this.getCurrentLevel(email);
      const unlocked = Object.entries(ACCENT_REQUIREMENTS)
        .filter(([, requiredLevel]) => level >= requiredLevel)
        .map(([accent]) => accent as AccentType);
      // Asegurar que Francia siempre esté incluido
      if (!unlocked.includes('Francia')) {
        unlocked.push('Francia');
      }
      return unlocked;
    } catch (error) {
      console.error('Error al obtener acentos desbloqueados:', error);
      return ['Francia']; // Retornar al menos Francia en caso de error
    }
  }

  /**
   * Obtener personalidades desbloqueadas
   * Neutral siempre está desbloqueada
   */
  async getUnlockedPersonalities(email: string): Promise<PersonalityType[]> {
    try {
      const level = await this.getCurrentLevel(email);
      const unlocked = Object.entries(PERSONALITY_REQUIREMENTS)
        .filter(([, requiredLevel]) => level >= requiredLevel)
        .map(([personality]) => personality as PersonalityType);
      // Asegurar que Neutral siempre esté incluido
      if (!unlocked.includes('Neutral')) {
        unlocked.push('Neutral');
      }
      return unlocked;
    } catch (error) {
      console.error('Error al obtener personalidades desbloqueadas:', error);
      return ['Neutral']; // Retornar al menos Neutral en caso de error
    }
  }

  /**
   * Obtener nivel requerido para desbloquear un acento
   */
  getRequiredLevelForAccent(accent: AccentType): number {
    return ACCENT_REQUIREMENTS[accent] || 0;
  }

  /**
   * Obtener nivel requerido para desbloquear una personalidad
   */
  getRequiredLevelForPersonality(personality: PersonalityType): number {
    return PERSONALITY_REQUIREMENTS[personality] || 0;
  }

  /**
   * Obtener tiempo total de práctica
   */
  async getTotalPracticeTime(email: string): Promise<number> {
    try {
      const progress = await supabaseService.getUserProgress(email);
      let totalSeconds = progress?.total_time_seconds || 0;
      

      // Sumar tiempo de sesión actual si está disponible
      try {
        const sessionTime = interactionTimeService.getTimeElapsed();
        totalSeconds += sessionTime;
      } catch (error) {
      }


      return totalSeconds;
    } catch (error) {
      console.error('Error al obtener tiempo total:', error);
      return 0;
    }
  }

  /**
   * Actualizar progreso del usuario
   */
  async updateUserProgress(email: string, timeSeconds: number, interactions: number): Promise<void> {
    try {
      await supabaseService.updateUserProgress(email, {
        total_time_seconds: timeSeconds,
        total_interactions: interactions,
      });

      // Verificar si subió de nivel
      await this.checkLevelUp(email);
    } catch (error) {
      console.error('Error al actualizar progreso:', error);
    }
  }

  /**
   * Actualizar contador de racha de 5
   */
  async updateStreak5Count(email: string, count: number): Promise<void> {
    try {
      await supabaseService.updateUserProgress(email, {
        streak_5_count: count,
      });
    } catch (error) {
      console.error('Error al actualizar contador de racha de 5:', error);
    }
  }
}

export const gamificationService = new GamificationService();
export default gamificationService;

